import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { PageHeader } from '../components/ui/index.js';

interface Trial {
  id: string;
  name: string;
  slug: string;
  status: string;
  subscription_status: string;
  trial_ends_at: string | null;
  lead_count: number;
  admin_email: string | null;
}

export function AdminTrialsPage() {
  const { toast } = useToast();
  const [trials, setTrials] = useState<Trial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'expiring' | 'expired'>('all');

  const fetchTrials = useCallback(async () => {
    setIsLoading(true);
    try {
      const d = await apiFetch<{ trials: Trial[] }>('/admin/trials');
      setTrials(d.trials);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchTrials(); }, [fetchTrials]);

  const handleExtend = async (orgId: string, orgName: string, days = 14) => {
    try {
      await apiFetch(`/admin/trials/${orgId}/extend`, { method: 'POST', body: JSON.stringify({ days }) });
      toast('success', `${orgName} extended by ${days} days`);
      fetchTrials();
    } catch { toast('error', 'Failed to extend trial'); }
  };

  const now = Date.now();
  const filtered = trials.filter(t => {
    if (filter === 'all') return true;
    if (!t.trial_ends_at) return filter === 'expired';
    const daysLeft = Math.ceil((new Date(t.trial_ends_at).getTime() - now) / 86400000);
    if (filter === 'expiring') return daysLeft > 0 && daysLeft <= 7;
    if (filter === 'expired') return daysLeft <= 0;
    return true;
  });

  return (
    <div className="space-y-5">
      <PageHeader title="Trial Management" subtitle={`${trials.length} trial accounts`} />

      <div className="flex gap-2">
        {(['all', 'expiring', 'expired'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              filter === f ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}>{f === 'all' ? `All (${trials.length})` : f === 'expiring' ? 'Expiring <7d' : 'Expired'}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-center py-12 text-sm text-gray-500">No trials match this filter.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => {
            const daysLeft = t.trial_ends_at ? Math.ceil((new Date(t.trial_ends_at).getTime() - now) / 86400000) : null;
            const isExpired = daysLeft !== null && daysLeft <= 0;
            const isUrgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 3;
            return (
              <div key={t.id} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{t.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        isExpired ? 'bg-red-100 text-red-700' : isUrgent ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {isExpired ? 'Expired' : daysLeft !== null ? `${daysLeft}d left` : 'No expiry'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t.admin_email ?? 'No admin'} | {t.lead_count} leads
                      {t.trial_ends_at && ` | Expires ${new Date(t.trial_ends_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleExtend(t.id, t.name, 7)}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium">+7d</button>
                    <button onClick={() => handleExtend(t.id, t.name, 14)}
                      className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-primary-700">+14d</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
